import { create } from 'zustand';
import type { ThemePref } from '@lira/shared';
import { apiPatch } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';

/**
 * Preferência de tema. A FONTE DA VERDADE é o servidor (coluna users.theme,
 * exposta em PublicUser e persistida via PATCH /api/auth/preferences). Este store
 * é o espelho de UI + cache local:
 * - localStorage 'lira-theme' guarda a preferência crua p/ o <script> no index.html
 *   pintar sem flash antes do primeiro paint;
 * - `hydrateFromUser` sincroniza com o valor do servidor no login/refresh/me;
 * - `setPreference` (toggle da UI) aplica na hora, grava local e persiste no servidor.
 */

const STORAGE_KEY = 'lira-theme';
type Resolved = 'light' | 'dark';

const media =
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-color-scheme: dark)')
    : null;

function systemResolved(): Resolved {
  return media?.matches ? 'dark' : 'light';
}

function resolve(pref: ThemePref): Resolved {
  return pref === 'system' ? systemResolved() : pref;
}

/** Escreve o atributo que a folha de estilo lê (`:root[data-theme='dark']`). */
function applyResolved(pref: ThemePref): Resolved {
  const r = resolve(pref);
  if (typeof document !== 'undefined') document.documentElement.dataset.theme = r;
  return r;
}

function readStored(): ThemePref {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'light' || v === 'dark' || v === 'system') return v;
  } catch {
    /* localStorage indisponível */
  }
  return 'system';
}

function writeStored(pref: ThemePref): void {
  try {
    localStorage.setItem(STORAGE_KEY, pref);
  } catch {
    /* ignore */
  }
}

interface ThemeState {
  preference: ThemePref;
  /** Tema efetivo aplicado ('system' já resolvido pelo prefers-color-scheme). */
  resolved: Resolved;
  /** Troca da UI: aplica, grava local e persiste no servidor (otimista). */
  setPreference: (pref: ThemePref) => void;
  /** Sincroniza com o servidor (login/refresh/me) sem re-gravar no servidor. */
  hydrateFromUser: (pref: ThemePref) => void;
}

const initialPref = readStored();

export const useThemeStore = create<ThemeState>((set, get) => ({
  preference: initialPref,
  resolved: applyResolved(initialPref),

  setPreference: (pref) => {
    if (pref === get().preference) {
      // mesma preferência mas 'system' pode ter mudado de resolução — reaplica
      set({ resolved: applyResolved(pref) });
      return;
    }
    writeStored(pref);
    set({ preference: pref, resolved: applyResolved(pref) });

    // Otimista: reflete já no usuário em memória e persiste no servidor.
    const { user } = useAuthStore.getState();
    if (user) useAuthStore.setState({ user: { ...user, theme: pref } });
    void apiPatch('/auth/preferences', { theme: pref }).catch(() => {
      /* silencioso: a UI já reflete; o próximo /me reconcilia se preciso */
    });
  },

  hydrateFromUser: (pref) => {
    if (pref === get().preference) return;
    writeStored(pref);
    set({ preference: pref, resolved: applyResolved(pref) });
  },
}));

// Quando a preferência é 'system', segue as mudanças de tema do SO em tempo real.
media?.addEventListener('change', () => {
  if (useThemeStore.getState().preference === 'system') {
    useThemeStore.setState({ resolved: applyResolved('system') });
  }
});
